import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { PreloaderType, UiService } from 'app/core/services/ui.service';
import { AuthService } from '../auth.service';
import { environment } from 'environments/environment';
import { AuthActivateQuestionsService } from '../auth-activate-questions.service';
import { DynamicFormComponent, JwtService, UtilsService } from 'app/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lms-entry',
  templateUrl: './entry.component.html',
  styleUrls: ['./entry.component.scss']
})
export class EntryComponent implements OnInit {
  error: any;
  public isContentfulActive = environment.contentful.active;
  public questions: any[];
  public userData: any;
  public loading: boolean = false;
  private service: string;
  public isScorm: boolean;
  public done: boolean;
  disclaimerPrivacyAndTerms: boolean = false;
  disclaimerMarketing: boolean = false;
  clientDisclaimerPrivacy: boolean = false;
  clientTermsUrl: string;
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;

  constructor(
    public authService: AuthService,
    public route: ActivatedRoute,
    public uiService: UiService,
    public jwtService: JwtService,
    public questionsService: AuthActivateQuestionsService,
    public router: Router,
    public utilsService: UtilsService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.uiService.displayPreloader(PreloaderType.ProgressBarIndeterminate);
    this.authService.logout();
    this.route.queryParamMap.subscribe((queryParams: ParamMap) => {
      const token = queryParams.get('token');
      const {
        clientId = null,
        sid = null,
        scorm = null,
        queryParams: serviceQueryParams = null
      } = this.jwtService.getTokenData(token);
      // eslint-disable-next-line
      console.info(`System entry, token ${token ? 'ok' : 'fail'} ${scorm ? '(scorm)' : ''}`);
      this.isScorm = scorm;
      this.authService.createServiceLogin({ token, clientId, sid }).subscribe({
        next: (response) => {
          this.uiService.hidePreloader();
          this.setServiceUrl(response.service, serviceQueryParams);
          const data = this.jwtService.getTokenData(response.token);

          if (data.firstSession || data.mustAcceptClientTerms) {
            this.userData = data.userData;
            this.questions = this.questionsService.getUserAndLangQuestions(data.userData);
            const lang = this.userData?.lang || this.translate.getBrowserLang();
            const { clientTermsUrls } = this.userData;
            this.clientTermsUrl = clientTermsUrls[lang]
              ? clientTermsUrls[lang]
              : clientTermsUrls['en'];
          } else {
            // eslint-disable-next-line
            window.location.replace(this.service);
          }
        },
        error: (error) => {
          // eslint-disable-next-line
          console.error('System entry error: ', error);
          this.uiService.hidePreloader();
          // eslint-disable-next-line
          const errorParam = encodeURIComponent(btoa(JSON.stringify(error)));
          this.router.navigate([`/landings/error`], {
            queryParams: { error: errorParam }
          });
        }
      });
    });
  }

  onSubmit(): void {
    const data = this.dynamicForm.form.value;
    this.error = null;
    this.loading = true;
    this.authService.updateSsoUser(this.userData.userId, data).subscribe({
      next: () => {
        const complianceDate = Date.now();
        const marketingComplianceDate = this.disclaimerMarketing ? Date.now() : undefined;
        const clientComplianceDate = this.clientDisclaimerPrivacy ? Date.now() : undefined;
        this.authService
          .updateSecurityUser(this.userData.userId, {
            complianceDate,
            marketingComplianceDate,
            clientComplianceDate
          })
          .subscribe(() => {
            // eslint-disable-next-line
            window.location.replace(this.service);
          });
      },
      error: (error) => {
        this.onError(error);
      }
    });
  }

  get submitDisabled(): any {
    if (this.dynamicForm) {
      return (
        this.dynamicForm.form.invalid ||
        this.loading ||
        this.done ||
        (this.clientTermsUrl && !this.clientDisclaimerPrivacy) ||
        !this.disclaimerPrivacyAndTerms
      );
    }
    return true;
  }

  // eslint-disable-next-line
  onError(error: any): void {
    this.loading = false;
    this.error = error
      ? Object.assign({}, error, {
          errorCode: `apiErrors.${error.errorCode || 'SYSTEM_ERROR'}`
        })
      : null;
  }

  setServiceUrl(serviceUrl: string, serviceQueryParams: any): void {
    const queryString = new URLSearchParams(serviceQueryParams).toString();
    const searchSeparator = serviceUrl.includes('?') ? '&' : '?';
    const search = queryString ? `${searchSeparator}${queryString}` : '';
    this.service = `${serviceUrl}${search}`;
  }
}
